// ---------------------------------------------------------------------------
// Access resolver — computes effective permissions from roles + overrides
// ---------------------------------------------------------------------------
//
// Effective = union(roles.permissions) + additions - removals

/**
 * Create an access resolver.
 *
 * @param {import('../../contracts/access.js').AccessData} data
 * @returns {import('../../contracts/access.js').AccessResolver}
 */
export function createAccessResolver(data) {
  /** @type {Set<string>} */
  let effectivePerms = computeEffective(data);

  /**
   * @param {import('../../contracts/access.js').AccessData} accessData
   * @returns {Set<string>}
   */
  function computeEffective(accessData) {
    const perms = new Set();

    // Union of all role permissions
    for (const role of accessData.roles) {
      for (const perm of role.permissions) {
        perms.add(perm);
      }
    }

    // Add per-user additions
    for (const perm of accessData.userOverrides.additions) {
      perms.add(perm);
    }

    // Remove per-user removals
    for (const perm of accessData.userOverrides.removals) {
      perms.delete(perm);
    }

    return perms;
  }

  function getEffectivePermissions() {
    return new Set(effectivePerms);
  }

  /**
   * @param {string} perm
   * @returns {boolean}
   */
  function hasPermission(perm) {
    return effectivePerms.has(perm);
  }

  /**
   * Filter menu tree by permissions. Returns a new tree (never mutates input).
   * Keeps leaf if permission OK or no permission required.
   * Keeps parent if it has at least one visible child.
   *
   * @param {import('../../contracts/menu.js').MenuNode[]} nodes
   * @returns {import('../../contracts/menu.js').MenuNode[]}
   */
  function filterMenu(nodes) {
    /** @type {import('../../contracts/menu.js').MenuNode[]} */
    const result = [];

    for (const node of nodes) {
      const filteredChildren = filterMenu(node.children);

      // Parent node: keep if it has visible children
      if (node.children.length > 0) {
        if (filteredChildren.length === 0) continue;
        result.push({ ...node, children: filteredChildren });
        continue;
      }

      // Leaf node: check permission
      if (node.permission && !effectivePerms.has(node.permission)) {
        continue;
      }

      result.push({ ...node, children: [] });
    }

    return result;
  }

  /**
   * @param {import('../../contracts/access.js').AccessData} accessData
   */
  function update(accessData) {
    effectivePerms = computeEffective(accessData);
  }

  return { getEffectivePermissions, hasPermission, filterMenu, update };
}
